import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Phone, DollarSign, ArrowRight, Clapperboard, Car, Truck, Crown, Zap, Star, Heart, Gem,
} from 'lucide-react';
import { matchToRateSheet, classifyBody } from '../utils/rateSheetMatch';

const API = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url?.startsWith('http') ? url : `${API}${url}`;

const CHAUFFEURED_SUBGROUPS = [
  { key: 'sedan',  label: 'Sedans',             icon: Car },
  { key: 'suv',    label: 'SUVs',               icon: Truck },
  { key: 'sports', label: 'Exotic Sports',      icon: Crown },
  { key: 'van',    label: 'Sprinters & Vans',   icon: Truck },
  { key: 'limo',   label: 'Stretch Limousines', icon: Car },
];
const SUBGROUP_LABELS = Object.fromEntries(CHAUFFEURED_SUBGROUPS.map(s => [s.key, s.label]));

const OCCASION_BANNERS = [
  { key: 'wedding', slug: 'wedding-getaway', title: 'Wedding Getaway', desc: 'Rolls-Royce, Maybach & Bentley — ribbon-ready send-offs and bridal party shuttles.', icon: Heart },
  { key: 'exotic', slug: 'exotic-collection', title: 'The Exotic Collection', desc: 'Lamborghini, Ferrari & V12 statements for birthdays, shoots, and grand entrances.', icon: Gem },
];

const FleetGallery = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mediaEntries, setMediaEntries] = useState([]);
  const [pricingVehicles, setPricingVehicles] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [partnerOrgs, setPartnerOrgs] = useState([]);
  const [section, setSection] = useState('chauffeured'); // 'chauffeured' | 'selfdrive'
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/fleet-media/all`).then(r => r.ok ? r.json() : { vehicles: [] }),
      fetch(`${API}/api/pricing/vehicles`).then(r => r.ok ? r.json() : { vehicles: [] }),
      fetch(`${API}/api/rentals/fleet`).then(r => r.ok ? r.json() : { vehicles: [] }),
      fetch(`${API}/api/partner-network/orgs`).then(r => r.ok ? r.json() : { orgs: [] }),
    ]).then(([mediaData, pricingData, rentalData, partnersData]) => {
      setMediaEntries(mediaData.vehicles || []);
      setPricingVehicles((pricingData.vehicles || pricingData) || []);
      setRentals(rentalData.vehicles || []);
      setPartnerOrgs(partnersData.orgs || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const partnerColorByOrg = useMemo(() => {
    const m = {};
    partnerOrgs.forEach(o => { m[o.org_id] = o.brand_color || '#0ea5e9'; });
    return m;
  }, [partnerOrgs]);

  const chauffeured = useMemo(() => {
    const enriched = mediaEntries.map(m => {
      const match = matchToRateSheet(m.vehicle_name || m.vehicle_slug, pricingVehicles);
      const isRental = match && match.category === 'rental';
      return {
        name: m.vehicle_name || m.vehicle_slug,
        slug: m.vehicle_slug,
        body: classifyBody(m.vehicle_name || m.vehicle_slug),
        onRateSheet: !!match && !isRental,
        retail: !isRental ? (match?.retail_hourly || 0) : 0,
        member: !isRental ? (match?.member_hourly || 0) : 0,
        minHrs: !isRental ? (match?.min_hours || 0) : 0,
        partnerOrgName: match?.partner_org_name || null,
        partnerBrand: match ? partnerColorByOrg[match.partner_org_id] : null,
        thumbnail: (m.images || [])[0]?.url || null,
        photoCount: (m.images || []).length,
        isRental,
      };
    }).filter(v => !v.isRental && v.thumbnail && !/collection|getaway fleet/i.test(v.name));
    enriched.sort((a, b) => {
      if (a.onRateSheet !== b.onRateSheet) return a.onRateSheet ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    return enriched;
  }, [mediaEntries, pricingVehicles, partnerColorByOrg]);

  const filteredChauffeured = filter === 'all' ? chauffeured : chauffeured.filter(v => v.body === filter);
  const availableSubgroups = ['all', ...CHAUFFEURED_SUBGROUPS.filter(sg => chauffeured.some(v => v.body === sg.key)).map(sg => sg.key)];

  const selfDrive = useMemo(() => {
    const list = rentals.map(r => ({
      name: r.vehicle,
      id: r.rental_id,
      category: r.category,
      thumbnail: r.thumbnail_url || (r.gallery || [])[0] || null,
      photoCount: (r.gallery || []).length,
      rate: r.retail_rate,
      note: r.note,
      description: r.description,
    })).filter(v => v.thumbnail);
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [rentals]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="fleet-gallery-page">
      {/* Hero */}
      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-950 to-black" />
        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center">
          <p className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase mb-6">The ACE Fleet</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6" data-testid="fleet-hero-title">
            Arrive In<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-amber-500">Something Special.</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto mb-8 leading-relaxed">
            Every vehicle inspected, detailed, and prepared to the ACE standard — chauffeured or self-drive.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/rates')} className="h-12 px-6 bg-red-600 hover:bg-red-700 text-white text-sm font-bold" data-testid="gallery-view-rates-btn">
              <DollarSign className="h-4 w-4 mr-2" /> View Rates
            </Button>
            <Button onClick={() => navigate('/fleet/virtual-tour')} variant="outline" className="h-12 px-6 border-gray-700 text-gray-300 hover:bg-gray-900 text-sm font-bold" data-testid="gallery-virtual-tour-btn">
              <Clapperboard className="h-4 w-4 mr-2" /> Virtual Tour
            </Button>
          </div>
        </div>
      </section>

      {/* Section tabs */}
      <div className="border-t border-gray-900 bg-gray-950/70 sticky top-0 z-20 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-center gap-2 flex-wrap">
          <button
            onClick={() => { setSection('chauffeured'); setFilter('all'); }}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${section === 'chauffeured' ? 'bg-red-600 text-white' : 'bg-gray-900 text-gray-400 hover:bg-gray-800'}`}
            data-testid="section-chauffeured-btn"
          >
            Chauffeured Fleet <span className="ml-1 text-xs opacity-70">{chauffeured.length}</span>
          </button>
          <button
            onClick={() => setSection('selfdrive')}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${section === 'selfdrive' ? 'bg-red-600 text-white' : 'bg-gray-900 text-gray-400 hover:bg-gray-800'}`}
            data-testid="section-selfdrive-btn"
          >
            Self-Drive Rentals <span className="ml-1 text-xs opacity-70">{selfDrive.length}</span>
          </button>
        </div>
        {section === 'chauffeured' && availableSubgroups.length > 2 && (
          <div className="max-w-6xl mx-auto px-6 pb-3 flex flex-wrap gap-2 justify-center">
            {availableSubgroups.map(k => (
              <button key={k} onClick={() => setFilter(k)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${filter === k ? 'bg-white text-black' : 'bg-gray-900 text-gray-500 hover:bg-gray-800 hover:text-gray-300'}`}
                data-testid={`filter-${k}`}>
                {k === 'all' ? 'All Vehicles' : SUBGROUP_LABELS[k]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="h-10 w-10 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500 text-sm">Loading fleet...</p>
          </div>
        ) : section === 'chauffeured' ? (
          filteredChauffeured.length === 0 ? (
            <div className="text-center py-20 text-gray-500">No chauffeured vehicles in this category yet.</div>
          ) : (
            <section data-testid="chauffeured-sections">
              {/* Occasion collection banners */}
              {filter === 'all' && (
                <div className="grid sm:grid-cols-2 gap-5 mb-10" data-testid="occasion-banners">
                  {OCCASION_BANNERS.map(b => {
                    const entry = mediaEntries.find(m => m.vehicle_slug === b.slug);
                    const img = entry?.images?.[0]?.url;
                    const BIcon = b.icon;
                    return (
                      <button key={b.key} onClick={() => navigate(`/occasions/${b.key}`)}
                        className="relative h-44 rounded-xl overflow-hidden group text-left border border-gray-800 hover:border-red-600 transition-all"
                        data-testid={`occasion-banner-${b.key}`}>
                        {img && <img src={resolveUrl(img)} alt={b.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/20" />
                        <div className="relative z-10 p-5 h-full flex flex-col justify-end">
                          <p className="text-[9px] uppercase tracking-[0.25em] text-red-500 font-black mb-1 flex items-center gap-1.5"><BIcon className="h-3 w-3" /> Occasion Collection</p>
                          <h3 className="text-lg font-black text-white">{b.title}</h3>
                          <p className="text-[11px] text-gray-300 max-w-xs leading-relaxed mb-2">{b.desc}</p>
                          <span className="text-xs font-bold text-red-400 flex items-center gap-1">Shop the collection <ArrowRight className="h-3 w-3" /></span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center gap-3 mb-8">
                <Crown className="h-5 w-5 text-red-500" />
                <div>
                  <h2 className="text-xl font-bold">ACE Chauffeured Fleet</h2>
                  <p className="text-xs text-gray-500">Professional chauffeur included · hourly rates</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredChauffeured.map(v => (
                  <Card key={v.slug}
                    className="bg-gray-950 border-gray-800 overflow-hidden group hover:border-gray-600 transition-all cursor-pointer"
                    onClick={() => navigate(`/fleet/${v.slug}`)}
                    data-testid={`vehicle-card-${v.slug}`}>
                    <div className="relative h-44 overflow-hidden bg-gray-900">
                      <img
                        src={resolveUrl(v.thumbnail)}
                        alt={v.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3 flex items-center gap-1.5">
                        <span className="text-[9px] font-bold bg-black/70 backdrop-blur-sm text-gray-300 px-2.5 py-1 rounded-full uppercase tracking-wider">
                          {SUBGROUP_LABELS[v.body] || v.body}
                        </span>
                        {v.onRateSheet && (
                          <span className="text-[9px] font-bold bg-red-600 text-white px-2 py-1 rounded-full uppercase tracking-wider inline-flex items-center gap-1" data-testid={`rate-sheet-badge-${v.slug}`}>
                            <Star className="h-2.5 w-2.5" /> Rate Sheet
                          </span>
                        )}
                      </div>
                      {v.photoCount > 1 && (
                        <span className="absolute bottom-3 right-3 text-[9px] font-bold bg-black/70 backdrop-blur-sm text-gray-300 px-2 py-1 rounded-full">{v.photoCount} photos</span>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-3 gap-2">
                        <div className="min-w-0">
                          <p className="font-bold text-white text-sm group-hover:text-red-500 transition-colors truncate">{v.name}</p>
                          {v.partnerOrgName && (
                            <span
                              className="mt-1 inline-flex text-[9px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border"
                              style={{ background: `${v.partnerBrand || '#0ea5e9'}15`, color: v.partnerBrand || '#0ea5e9', borderColor: `${v.partnerBrand || '#0ea5e9'}40` }}
                              data-testid={`partner-badge-${v.slug}`}
                            >
                              via {v.partnerOrgName}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-end justify-between">
                        <div>
                          {v.retail > 0 ? (
                            <>
                              <div className="flex items-baseline gap-1">
                                <p className="text-xl font-black text-white">${v.retail}</p>
                                <p className="text-[10px] text-gray-500">/hr chauffeured</p>
                              </div>
                              {v.member > 0 && v.member < v.retail && (
                                <p className="text-[10px] text-red-400 font-semibold">from ${v.member}/hr member</p>
                              )}
                            </>
                          ) : (
                            <p className="text-sm font-black text-white">By Request</p>
                          )}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/booking?vehicle=${v.slug}`); }}
                          className="inline-flex items-center gap-1 text-xs font-bold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-full transition-colors"
                          data-testid={`book-btn-${v.slug}`}
                        >
                          Book <ArrowRight className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          )
        ) : (
          selfDrive.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <p>No self-drive rental photos available yet.</p>
              <Button onClick={() => navigate('/rentals')} className="mt-4 bg-red-600 hover:bg-red-700">Browse Rentals</Button>
            </div>
          ) : (
            <section data-testid="selfdrive-section">
              <div className="flex items-center gap-3 mb-8">
                <Zap className="h-5 w-5 text-red-500" />
                <div>
                  <h2 className="text-xl font-bold">ACE Self-Drive Fleet</h2>
                  <p className="text-xs text-gray-500">You drive · published daily rates</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {selfDrive.map(v => (
                  <Card key={v.id}
                    className="bg-gray-950 border-gray-800 overflow-hidden group hover:border-gray-600 transition-all cursor-pointer"
                    onClick={() => navigate(`/rentals/${v.id}`)}
                    data-testid={`vehicle-card-${v.id}`}>
                    <div className="relative h-44 overflow-hidden bg-gray-900">
                      <img src={resolveUrl(v.thumbnail)} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      <div className="absolute top-3 left-3">
                        <span className="text-[9px] font-bold bg-black/70 backdrop-blur-sm text-gray-300 px-2.5 py-1 rounded-full uppercase tracking-wider">{v.category}</span>
                      </div>
                      {v.photoCount > 1 && (
                        <span className="absolute bottom-3 right-3 text-[9px] font-bold bg-black/70 backdrop-blur-sm text-gray-300 px-2 py-1 rounded-full">{v.photoCount} photos</span>
                      )}
                    </div>
                    <div className="p-5">
                      <p className="font-bold text-white text-sm group-hover:text-red-500 transition-colors mb-3">{v.name}</p>
                      <div className="flex items-end justify-between">
                        <div className="flex items-baseline gap-1">
                          <p className="text-xl font-black text-white">${v.rate}</p>
                          <p className="text-[10px] text-gray-500">/day · by request</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/rentals/request?vehicle=${v.id}`); }}
                          className="inline-flex items-center gap-1 text-xs font-bold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-full transition-colors"
                          data-testid={`request-btn-${v.id}`}
                        >
                          Request <ArrowRight className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          )
        )}
      </div>

      {/* Bottom CTA */}
      <section className="py-16 border-t border-gray-900 text-center">
        <div className="max-w-lg mx-auto px-6">
          <Zap className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-2">Like What You See?</h3>
          <p className="text-sm text-gray-500 mb-6">View the full rate card or book your vehicle directly.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/rates')} className="h-11 px-6 bg-red-600 hover:bg-red-700 text-white text-sm font-bold" data-testid="gallery-rates-cta">
              View Rates <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <a href="tel:6788463009" className="inline-flex items-center justify-center h-11 px-6 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-900 text-sm font-bold" data-testid="gallery-book-cta">
              <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FleetGallery;
